import { progressStorage, migrateFromAsyncStorage } from '@/services/progressStorage';

export interface CachedViewingProgress {
  episodeId: string;
  completionPercentage: number;
  isCompleted: boolean;
  updatedAt: string;
  episodeTitle?: string;
  episodeDescription?: string;
  episodeDuration?: number;
  episodePosition?: number;
  seasonPosition?: number;
  seasonId?: string;
  seriesId?: string;
  seriesTitle?: string;
  seriesThumbnail?: string | null;
  videoUrl?: string;
}

interface CacheStore {
  [episodeId: string]: CachedViewingProgress;
}

const CACHE_KEY = '@viewing_progress_cache';
const CACHE_RETENTION_DAYS = 30;
const DEBOUNCE_INTERVAL_MS = 10000;

const VERBOSE_CACHE_LOGS = false;

const verboseLog = (...args: any[]) => {
  if (__DEV__ && VERBOSE_CACHE_LOGS) {
    console.log(...args);
  }
};

class ViewingProgressCache {
  private cache: CacheStore = {};
  private seriesIndex: Map<string, Set<string>> = new Map();
  private initialized = false;
  private initializationPromise: Promise<void> | null = null;

  private isDirty = false;
  private debounceTimer: ReturnType<typeof setTimeout> | null = null;
  private lastPersistTime = 0;

  async initialize(): Promise<void> {
    if (this.initialized) {
      return;
    }

    if (this.initializationPromise) {
      return this.initializationPromise;
    }

    this.initializationPromise = this._initializeInternal();
    return this.initializationPromise;
  }

  private async _initializeInternal(): Promise<void> {
    const startTime = Date.now();
    console.log('[CACHE] Initializing viewing progress cache...');

    try {
      await migrateFromAsyncStorage();

      const storedCache = progressStorage.getString(CACHE_KEY);
      if (storedCache) {
        this.cache = JSON.parse(storedCache);
        const entryCount = Object.keys(this.cache).length;
        console.log(`[CACHE] Loaded ${entryCount} entries from MMKV storage`);

        await this.cleanupOldEntries();
        const cleanedCount = await this.cleanupEntriesWithoutSeriesId();
        if (cleanedCount > 0) {
          console.log(`[CACHE] Removed ${cleanedCount} legacy entries without seriesId`);
        }

        this.buildSeriesIndex();
      } else {
        console.log('[CACHE] No cached data found, starting fresh');
        this.cache = {};
        this.seriesIndex = new Map();
      }

      this.initialized = true;
      const duration = Date.now() - startTime;
      console.log(`[CACHE] Initialization complete in ${duration}ms (${this.seriesIndex.size} series indexed)`);
    } catch (error) {
      console.error('[CACHE] Error initializing cache:', error);
      this.cache = {};
      this.seriesIndex = new Map();
      this.initialized = true;
    } finally {
      this.initializationPromise = null;
    }
  }

  private buildSeriesIndex(): void {
    this.seriesIndex = new Map();
    const entries = Object.entries(this.cache);

    for (const [episodeId, entry] of entries) {
      if (entry.seriesId) {
        this.addToSeriesIndex(entry.seriesId, episodeId);
      }
    }

    verboseLog(`[CACHE-INDEX] Built index for ${this.seriesIndex.size} series`);
  }

  private addToSeriesIndex(seriesId: string, episodeId: string): void {
    if (!seriesId) return;

    let episodeSet = this.seriesIndex.get(seriesId);
    if (!episodeSet) {
      episodeSet = new Set();
      this.seriesIndex.set(seriesId, episodeSet);
    }
    episodeSet.add(episodeId);
  }

  private removeFromSeriesIndex(seriesId: string | undefined, episodeId: string): void {
    if (!seriesId) return;

    const episodeSet = this.seriesIndex.get(seriesId);
    if (episodeSet) {
      episodeSet.delete(episodeId);
      if (episodeSet.size === 0) {
        this.seriesIndex.delete(seriesId);
      }
    }
  }

  private async cleanupEntriesWithoutSeriesId(): Promise<number> {
    const entries = Object.entries(this.cache);
    let removedCount = 0;

    for (const [episodeId, entry] of entries) {
      if (!entry.seriesId || entry.seriesId === '') {
        delete this.cache[episodeId];
        removedCount++;
      }
    }

    if (removedCount > 0) {
      this.markDirtyAndSchedulePersist();
    }

    return removedCount;
  }

  private async cleanupOldEntries(): Promise<void> {
    const now = Date.now();
    const retentionMs = CACHE_RETENTION_DAYS * 24 * 60 * 60 * 1000;
    const entries = Object.entries(this.cache);
    let removedCount = 0;

    for (const [episodeId, entry] of entries) {
      const entryAge = now - new Date(entry.updatedAt).getTime();
      if (entryAge > retentionMs) {
        this.removeFromSeriesIndex(entry.seriesId, episodeId);
        delete this.cache[episodeId];
        removedCount++;
      }
    }

    if (removedCount > 0) {
      console.log(`[CACHE] Cleaned up ${removedCount} old entries (older than ${CACHE_RETENTION_DAYS} days)`);
      this.markDirtyAndSchedulePersist();
    }
  }

  private markDirtyAndSchedulePersist(): void {
    this.isDirty = true;

    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }

    this.debounceTimer = setTimeout(() => {
      this.persistCacheIfDirty();
    }, DEBOUNCE_INTERVAL_MS);
  }

  private persistCacheIfDirty(): void {
    if (!this.isDirty) {
      return;
    }

    try {
      const cacheString = JSON.stringify(this.cache);
      progressStorage.setString(CACHE_KEY, cacheString);
      this.isDirty = false;
      this.lastPersistTime = Date.now();

      if (__DEV__) {
        console.log(`[CACHE] Persisted to MMKV (${Object.keys(this.cache).length} entries)`);
      }
    } catch (error) {
      console.error('[CACHE] Error persisting cache:', error);
    }
  }

  flushIfDirty(): void {
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
      this.debounceTimer = null;
    }

    this.persistCacheIfDirty();
  }

  async getEpisodeProgress(episodeId: string): Promise<CachedViewingProgress | null> {
    await this.initialize();

    const progress = this.cache[episodeId];
    if (progress) {
      verboseLog(`[CACHE] HIT for episode ${episodeId}`);
      return progress;
    }

    verboseLog(`[CACHE] MISS for episode ${episodeId}`);
    return null;
  }

  async getSeriesProgress(seriesId: string): Promise<CachedViewingProgress[]> {
    await this.initialize();

    const startTime = Date.now();
    const episodeIds = this.seriesIndex.get(seriesId);

    if (!episodeIds || episodeIds.size === 0) {
      const duration = Date.now() - startTime;
      console.log(`[CACHE-SERIES] No episodes for series ${seriesId} (${duration}ms)`);
      return [];
    }

    const seriesProgress: CachedViewingProgress[] = [];
    for (const episodeId of episodeIds) {
      const entry = this.cache[episodeId];
      if (entry) {
        seriesProgress.push(entry);
      }
    }

    const duration = Date.now() - startTime;
    console.log(`[CACHE-SERIES] Found ${seriesProgress.length} episodes for series ${seriesId} (${duration}ms)`);

    verboseLog(`[CACHE-SERIES] Episodes:`, seriesProgress.map(ep => ({
      episodeId: ep.episodeId,
      completionPercentage: ep.completionPercentage,
      seasonPosition: ep.seasonPosition,
      episodePosition: ep.episodePosition
    })));

    return seriesProgress;
  }

  async getResumeEpisode(seriesId: string): Promise<{
    episodeId: string;
    startTimeSeconds: number;
    completionPercentage: number;
  } | null> {
    await this.initialize();

    const startTime = Date.now();
    const seriesProgress = await this.getSeriesProgress(seriesId);

    if (seriesProgress.length === 0) {
      return null;
    }

    verboseLog(`[CACHE-RESUME] Episodes before sorting:`,
      seriesProgress.map(ep => ({
        episodeId: ep.episodeId,
        seasonPosition: ep.seasonPosition,
        episodePosition: ep.episodePosition,
        completionPercentage: ep.completionPercentage
      }))
    );

    const sortedEpisodes = seriesProgress.sort((a, b) => {
      const aSeasonPos = a.seasonPosition ?? 1;
      const bSeasonPos = b.seasonPosition ?? 1;
      if (aSeasonPos !== bSeasonPos) {
        return aSeasonPos - bSeasonPos;
      }
      const aEpisodePos = a.episodePosition ?? 1;
      const bEpisodePos = b.episodePosition ?? 1;
      return aEpisodePos - bEpisodePos;
    });

    const unfinishedEpisode = sortedEpisodes.find((ep) => ep.completionPercentage < 90);

    if (!unfinishedEpisode) {
      const duration = Date.now() - startTime;
      console.log(`[CACHE-RESUME] All ${sortedEpisodes.length} episodes completed for series ${seriesId} (${duration}ms)`);
      return null;
    }

    const episodeDuration = unfinishedEpisode.episodeDuration ?? 0;
    const currentPositionSeconds = (unfinishedEpisode.completionPercentage / 100) * episodeDuration;
    const rewindPercentage = episodeDuration * 0.01;
    const startTimeSeconds = Math.max(0, currentPositionSeconds - rewindPercentage);

    const result = {
      episodeId: unfinishedEpisode.episodeId,
      startTimeSeconds,
      completionPercentage: unfinishedEpisode.completionPercentage,
    };

    const duration = Date.now() - startTime;
    console.log(`[CACHE-RESUME] Resume found: ep ${result.episodeId} at ${result.completionPercentage}% (${duration}ms)`);

    return result;
  }

  async updateEpisodeProgress(progress: CachedViewingProgress): Promise<void> {
    await this.initialize();

    if (__DEV__ && (!progress.seriesId || progress.seriesId === '')) {
      console.warn(`[CACHE-UPDATE] Missing seriesId for episode ${progress.episodeId}`);
    }

    const existingProgress = this.cache[progress.episodeId];

    if (existingProgress) {
      const existingTimestamp = new Date(existingProgress.updatedAt).getTime();
      const newTimestamp = new Date(progress.updatedAt).getTime();

      if (newTimestamp < existingTimestamp) {
        verboseLog(`[CACHE-UPDATE] Skipping update - existing data is newer`);
        return;
      }

      if (existingProgress.seriesId && existingProgress.seriesId !== progress.seriesId) {
        this.removeFromSeriesIndex(existingProgress.seriesId, progress.episodeId);
      }
    }

    this.cache[progress.episodeId] = progress;

    if (progress.seriesId) {
      this.addToSeriesIndex(progress.seriesId, progress.episodeId);
    }

    verboseLog(`[CACHE-UPDATE] Updated ep ${progress.episodeId} - ${progress.completionPercentage}%`);

    this.markDirtyAndSchedulePersist();
  }

  async bulkUpdateProgress(progressList: CachedViewingProgress[]): Promise<void> {
    await this.initialize();

    let updatedCount = 0;

    for (const progress of progressList) {
      const existingProgress = this.cache[progress.episodeId];

      if (existingProgress) {
        const existingTimestamp = new Date(existingProgress.updatedAt).getTime();
        const newTimestamp = new Date(progress.updatedAt).getTime();

        if (newTimestamp < existingTimestamp) {
          continue;
        }

        if (existingProgress.seriesId && existingProgress.seriesId !== progress.seriesId) {
          this.removeFromSeriesIndex(existingProgress.seriesId, progress.episodeId);
        }
      }

      this.cache[progress.episodeId] = progress;

      if (progress.seriesId) {
        this.addToSeriesIndex(progress.seriesId, progress.episodeId);
      }

      updatedCount++;
    }

    if (updatedCount > 0) {
      console.log(`[CACHE] Bulk updated ${updatedCount} episodes`);
      this.markDirtyAndSchedulePersist();
    }
  }

  async clearCache(): Promise<void> {
    console.log('[CACHE] Clearing entire cache');
    this.cache = {};
    this.seriesIndex = new Map();
    this.isDirty = false;

    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
      this.debounceTimer = null;
    }

    progressStorage.delete(CACHE_KEY);
  }

  async clearSeriesCache(seriesId: string): Promise<void> {
    await this.initialize();

    const episodeIds = this.seriesIndex.get(seriesId);
    if (!episodeIds || episodeIds.size === 0) {
      console.log(`[CACHE] No episodes to clear for series ${seriesId}`);
      return;
    }

    const count = episodeIds.size;
    for (const episodeId of episodeIds) {
      delete this.cache[episodeId];
    }

    this.seriesIndex.delete(seriesId);

    console.log(`[CACHE] Cleared ${count} episodes for series ${seriesId}`);
    this.markDirtyAndSchedulePersist();
  }

  async getCacheStats(): Promise<{
    totalEntries: number;
    totalSeries: number;
    oldestEntry: string | null;
    newestEntry: string | null;
    cacheSizeKB: number;
  }> {
    await this.initialize();

    const entries = Object.values(this.cache);
    const totalEntries = entries.length;

    if (totalEntries === 0) {
      return {
        totalEntries: 0,
        totalSeries: 0,
        oldestEntry: null,
        newestEntry: null,
        cacheSizeKB: 0,
      };
    }

    const totalSeries = this.seriesIndex.size;

    const sortedByDate = [...entries].sort(
      (a, b) => new Date(a.updatedAt).getTime() - new Date(b.updatedAt).getTime()
    );

    const oldestEntry = sortedByDate[0].updatedAt;
    const newestEntry = sortedByDate[sortedByDate.length - 1].updatedAt;

    const cacheString = JSON.stringify(this.cache);
    const cacheSizeKB = Math.round((cacheString.length * 2) / 1024);

    return {
      totalEntries,
      totalSeries,
      oldestEntry,
      newestEntry,
      cacheSizeKB,
    };
  }

  isInitialized(): boolean {
    return this.initialized;
  }

  hasPendingWrites(): boolean {
    return this.isDirty;
  }

  getLastPersistTime(): number {
    return this.lastPersistTime;
  }
}

export const viewingProgressCache = new ViewingProgressCache();
